import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  workspacesApi,
  getWorkspaceId,
  setWorkspaceId,
} from '../api/workspaces';
import { isAdmin } from '../api/client';

/**
 * Route guard that ensures a workspace is active before rendering child routes.
 *
 * Logic:
 * 1. If getWorkspaceId() returns a valid ID -> render children (workspace is active).
 * 2. If getWorkspaceId() is null but workspaces exist -> auto-activate the first
 *    workspace (call workspacesApi.activate + setWorkspaceId), then render children.
 * 3. If getWorkspaceId() is null and NO workspaces exist -> redirect to /workspaces.
 *
 * The /workspaces route itself is always accessible (passed as excludePath).
 */
export default function WorkspaceGuard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  const localWorkspaceId = getWorkspaceId();

  const { data: workspaces, isLoading, isError } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspacesApi.list,
    retry: false,
    staleTime: 30_000,
  });

  // Case 1: Workspace ID is in localStorage — but it may be STALE (a workspace
  // deleted/re-provisioned server-side: registry wipes, engine cut-overs). Every
  // request carrying a dead X-Workspace-Id 503s — the "companies there but zeros
  // everywhere" symptom. Validate against the live list; heal instead of failing.
  if (localWorkspaceId) {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-2 border-primary-200 border-t-primary-600 rounded-full mx-auto mb-3" />
            <p className="text-sm text-gray-500">Checking workspace...</p>
          </div>
        </div>
      );
    }
    if (!isError && Array.isArray(workspaces) && workspaces.length > 0
        && !workspaces.some((w: { id: string }) => w.id === localWorkspaceId)) {
      // Stored workspace no longer exists — replace with the active one or the first.
      const replacement: { id: string } | undefined = workspaces.find((w: { isActive: boolean }) => w.isActive) ?? workspaces[0];
      if (replacement) setWorkspaceId(replacement.id);
      return <>{children}</>; // re-renders with the fresh id; requests go out with it
    }
    return <>{children}</>;
  }

  // Still loading workspace list -- show a brief loading state.
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-sm text-gray-500">Checking workspaces...</p>
        </div>
      </div>
    );
  }

  // Case 2: No workspace in localStorage but workspaces exist -- auto-activate the first one.
  if (
    !localWorkspaceId &&
    !activated &&
    !activating &&
    workspaces &&
    workspaces.length > 0
  ) {
    // Find the currently active workspace from the server, or fall back to the first one.
    const activeFromServer = workspaces.find((w: { isActive: boolean }) => w.isActive);
    const toActivate = activeFromServer ?? workspaces[0]!;

    // Fire-and-forget activation -- the effect below handles the state transition.
    setActivating(true);
    workspacesApi.activate(toActivate.id)
      .then(() => {
        setWorkspaceId(toActivate.id);
        setActivated(true);
        setActivating(false);
      })
      .catch(() => {
        setActivating(false);
      });

    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-sm text-gray-500">Activating workspace...</p>
        </div>
      </div>
    );
  }

  // Still activating -- keep showing spinner.
  if (activating) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-sm text-gray-500">Activating workspace...</p>
        </div>
      </div>
    );
  }

  // Just finished activating (react will re-render with the workspace ID set).
  if (activated) {
    return <>{children}</>;
  }

  // Case 3: No workspace in localStorage and no workspaces exist (or API error).
  // Redirect to /workspaces, but don't redirect if already there.
  if (location.pathname === '/workspaces') {
    return <>{children}</>;
  }

  // No workspaces available -- redirect to the workspaces management page.
  // Non-admins cannot enter /workspaces (AdminRoute) — redirecting them would
  // bounce between /workspaces -> / -> guard -> /workspaces forever, so they
  // get the message without the redirect.
  if (!workspaces || workspaces.length === 0 || isError) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center max-w-md">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-700 mb-2">No Workspace Selected</h2>
          <p className="text-sm text-gray-500 mb-4">
            Please create or select a workspace to get started.
          </p>
          {isAdmin() && <Navigate to="/workspaces" replace />}
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
